import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import Dialog from 'components/common/Dialog/Dialog';
import Input from 'components/common/Input/Input';
import { Alert } from 'react-bootstrap';
import ELEMENT from 'common/element';
import { EDIT_SECRET, EDIT_SECRET_DESCRIPTION, SECRETS_DATA } from '../queries';
import { SecretAndDeselect } from '../types';
import { isDescriptionValidRegex, isNameValidRegex, isSecretValidRegex } from '../consts';

import '../styles.scss';

type EditSecretDialogProps = {
  editSecretDialogIsOpen: boolean;
  setEditSecretDialogIsOpen: (open: boolean) => void;
  handleChange: () => void;
} & SecretAndDeselect;

/**
 *
 * A dialog that edit Secrets
 *
 */
const EditSecretDialog: FC<EditSecretDialogProps> = ({
  handleChange,
  selectedSecret,
  deselectSecret,
  editSecretDialogIsOpen,
  setEditSecretDialogIsOpen,
}) => {
  const [editSecret] = useMutation(EDIT_SECRET, { refetchQueries: [SECRETS_DATA] });
  const [editSecretDescription] = useMutation(EDIT_SECRET_DESCRIPTION, { refetchQueries: [SECRETS_DATA] });

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [secretValue, setSecretValue] = useState<string>('');
  const [dirtySecret, setDirtySecret] = useState(false);

  useEffect(() => {
    if (selectedSecret) {
      setName(selectedSecret.name);
      setDescription(selectedSecret.description);
      setSecretValue('');
      setDirtySecret(false);
    }
  }, [selectedSecret]);

  const isNameValid = isNameValidRegex.test(name);
  const onNameChange = (event: SyntheticEvent) => {
    setName((event?.target as HTMLTextAreaElement)?.value);
  };
  const isDescriptionValid = isDescriptionValidRegex.test(description);
  const onDescriptionChange = (event: SyntheticEvent) => {
    setDescription((event?.target as HTMLTextAreaElement)?.value);
  };
  const isSecretValueValid = isSecretValidRegex.test(secretValue);
  const onSecretValueChange = (event: SyntheticEvent) => {
    const newValue = (event?.target as HTMLTextAreaElement)?.value;
    setSecretValue(newValue);
    setDirtySecret(newValue !== '' && isSecretValueValid);
  };
  const reset = (): void => {
    if (selectedSecret) {
      setName(selectedSecret.name);
      setDescription(selectedSecret.description);
      setSecretValue('');
      setDirtySecret(false);
    }
  };
  const onDialogSubmit = (): void => {
    if (dirtySecret) {
      editSecret({ variables: { name: name, description: description, secretValue: secretValue } });
    } else {
      editSecretDescription({ variables: { name: name, description: description } });
    }
    handleChange();
    reset();
  };
  const onClose = (): void => {
    reset();
    setEditSecretDialogIsOpen(false);
    deselectSecret();
  };

  if (selectedSecret) {
    return (
      <Dialog
        title="Edit Secret"
        className="SecretsPage__Modal"
        show={editSecretDialogIsOpen}
        close={onClose}
        okCallback={onDialogSubmit}
        okButtonText="Submit"
        testId={ELEMENT.editSecretDialog}
        size="xl"
      >
        <div>
          <Input
            type="text"
            label="Name"
            onChange={onNameChange}
            isInvalid={!isNameValid}
            value={name}
            placeholder="Name of your secret"
            testId={ELEMENT.newSecretInput}
            disabled={true}
            size="lg"
          />
          <Input
            type="text"
            label="Description"
            onChange={onDescriptionChange}
            isInvalid={!isDescriptionValid}
            value={description}
            placeholder="Description of your secret"
            testId={ELEMENT.newSecretInput}
            size="lg"
          />
          <Input
            type="text"
            label="New Secret Value"
            onChange={onSecretValueChange}
            isInvalid={!isSecretValueValid}
            value={secretValue}
            placeholder="Optional Update"
            testId={ELEMENT.newSecretInput}
            size="lg"
          />
          <Alert show={dirtySecret} variant="info">
            Secret will be updated
          </Alert>
          <Alert show={!dirtySecret} variant="info">
            Secret is unmodified
          </Alert>
        </div>
      </Dialog>
    );
  }
  return null;
};

export default EditSecretDialog;
